export type SpatialOverlay = "none" | "menu" | "conversation" | "controls";

export type SpatialGestureMode =
  | "gaze"
  | "open-conversation"
  | "open-controls"
  | "close-conversation"
  | "close-controls";

export interface SpatialRelease {
  overlay: SpatialOverlay;
  completed: boolean;
}

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

export function gestureModeAt(overlay: SpatialOverlay, y: number, height: number): SpatialGestureMode {
  if (overlay === "conversation") return "close-conversation";
  if (overlay === "controls") return "close-controls";
  if (overlay === "menu") return "gaze";
  if (y <= height * .3) return "open-controls";
  if (y >= height * .68) return "open-conversation";
  return "gaze";
}

export function spatialProgress(mode: SpatialGestureMode, dy: number, height: number): number {
  const travel = Math.max(84, height * .24);
  if (mode === "open-conversation") return clamp01(-dy / travel);
  if (mode === "open-controls") return clamp01(dy / travel);
  if (mode === "close-conversation") return 1 - clamp01(dy / travel);
  if (mode === "close-controls") return 1 - clamp01(-dy / travel);
  return 0;
}

export function resolveSpatialRelease(
  current: SpatialOverlay,
  mode: SpatialGestureMode,
  dy: number,
  velocityY: number,
  height: number,
): SpatialRelease {
  const progress = spatialProgress(mode, dy, height);
  const opening = mode === "open-conversation" || mode === "open-controls";
  const directionalFlick = mode === "open-conversation"
    ? velocityY < -.44
    : mode === "open-controls"
      ? velocityY > .44
      : mode === "close-conversation"
        ? velocityY > .44
        : mode === "close-controls"
          ? velocityY < -.44
          : false;

  if (mode === "gaze") return { overlay: current, completed: false };
  // The round display leaves very little travel above the top sheet handle. A
  // deliberate one-third pull is therefore the reversible commit threshold.
  const completed = opening ? progress >= .36 || directionalFlick : progress <= .72 || directionalFlick;
  if (!completed) return { overlay: current, completed: false };
  if (mode === "open-conversation") return { overlay: "conversation", completed: true };
  if (mode === "open-controls") return { overlay: "controls", completed: true };
  return { overlay: "none", completed: true };
}

export function overlayFromWire(value: "none" | "conversation" | "controls" | "action-ring" | "approval"): SpatialOverlay {
  if (value === "action-ring") return "menu";
  if (value === "conversation" || value === "controls") return value;
  return "none";
}

export function overlayToWire(value: SpatialOverlay): "none" | "conversation" | "controls" | "action-ring" {
  return value === "menu" ? "action-ring" : value;
}
